'use client';

/**
 * NotFoundState — "We can't find that page", with Home and Back.
 *
 * Used by `app/not-found.tsx` (outside the shell) and
 * `app/(app)/not-found.tsx` (inside it), and by a detail page whose record
 * answered 404:
 *
 *     if (query.error instanceof NotFoundError) return <NotFoundState title="We can't find that matter" />;
 */
import { ArrowLeft, SearchX } from 'lucide-react';
import Link from 'next/link';
import type { ReactNode } from 'react';

import { Button, buttonClassName } from './Button';
import { cn } from './cn';

export interface NotFoundStateProps {
  title?: ReactNode;
  message?: ReactNode;
  homeHref?: string;
  homeLabel?: string;
  /** Show a Back button (history.back). Default true. */
  showBack?: boolean;
  className?: string;
}

export function NotFoundState({
  title = 'We can’t find that page',
  message = 'The link may be wrong, or the page may have moved.',
  homeHref = '/dashboard',
  homeLabel = 'Go to home',
  showBack = true,
  className,
}: NotFoundStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-12 text-center',
        className,
      )}
      data-error-state="notFound"
    >
      <SearchX aria-hidden className="text-fg-muted" size={24} strokeWidth={1.75} />
      <h1 className="text-xl font-semibold text-fg">{title}</h1>
      <p className="max-w-md text-sm text-fg-secondary">{message}</p>
      <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
        <Link className={buttonClassName()} href={homeHref}>
          {homeLabel}
        </Link>
        {showBack ? (
          <Button
            leftIcon={ArrowLeft}
            variant="secondary"
            onClick={() => {
              if (typeof window !== 'undefined') window.history.back();
            }}
          >
            Back
          </Button>
        ) : null}
      </div>
    </div>
  );
}
